import { lazy, Suspense, useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { supabase } from './lib/supabase';
import { ThemeProvider } from './lib/ThemeContext';
import Header from './components/Header';
import Footer from './components/Footer';
import CursorSpotlight from './components/CursorSpotlight';
import ScrollProgress from './components/ScrollProgress';

// ─── Eagerly loaded (above the fold / tiny) ───────────────────────────────────
import LandingPage from './pages/LandingPage';

// ─── Lazy-loaded pages ────────────────────────────────────────────────────────
const ServicesPage              = lazy(() => import('./pages/ServicesPage'));
const PortfolioPage             = lazy(() => import('./pages/PortfolioPage'));
const AboutPage                 = lazy(() => import('./pages/AboutPage'));
const ContactPage               = lazy(() => import('./pages/ContactPage'));
const BlogPage                  = lazy(() => import('./pages/BlogPage'));
const PrivacyPolicyPage         = lazy(() => import('./pages/PrivacyPolicyPage'));
const TermsAndConditionsPage    = lazy(() => import('./pages/TermsAndConditionsPage'));
const GoogleMyBusinessPage      = lazy(() => import('./pages/GoogleMyBusinessPage'));
const AdminPanel                = lazy(() => import('./pages/AdminPanel'));
const AdminLoginPage            = lazy(() => import('./pages/AdminLoginPage'));

// Blog posts
const HowToBuildSaaSPlatform              = lazy(() => import('./pages/blog/HowToBuildSaaSPlatform'));
const SaaSDevelopmentCost                 = lazy(() => import('./pages/blog/SaaSDevelopmentCost'));
const BestTechStackSaaS2025               = lazy(() => import('./pages/blog/BestTechStackSaaS2025'));
const MicroSaaSIdeas2025                  = lazy(() => import('./pages/blog/MicroSaaSIdeas2025'));
const FirebaseVsSupabase                  = lazy(() => import('./pages/blog/FirebaseVsSupabase'));
const ChooseSoftwareDevelopmentCompany    = lazy(() => import('./pages/blog/ChooseSoftwareDevelopmentCompany'));
const ScalableWebAppArchitecture          = lazy(() => import('./pages/blog/ScalableWebAppArchitecture'));
const MVPvsFullProduct                    = lazy(() => import('./pages/blog/MVPvsFullProduct'));
const AISaaSIdeas2025                     = lazy(() => import('./pages/blog/AISaaSIdeas2025'));
const WebAppVsWebsite                     = lazy(() => import('./pages/blog/WebAppVsWebsite'));
const CRMDevelopmentGuide                 = lazy(() => import('./pages/blog/CRMDevelopmentGuide'));
const ERPDevelopmentGuide                 = lazy(() => import('./pages/blog/ERPDevelopmentGuide'));
const AndroidAppDevelopmentGuide          = lazy(() => import('./pages/blog/AndroidAppDevelopmentGuide'));
const SoftwareDevelopmentCompanyIndia     = lazy(() => import('./pages/blog/SoftwareDevelopmentCompanyIndia'));
const SoftwareDevelopmentCompanyPatna     = lazy(() => import('./pages/blog/SoftwareDevelopmentCompanyPatna'));
const LMSDevelopmentGuide                 = lazy(() => import('./pages/blog/LMSDevelopmentGuide'));

// Services
const SaaSDevelopmentPage       = lazy(() => import('./pages/services/SaaSDevelopmentPage'));
const WebAppDevelopmentPage     = lazy(() => import('./pages/services/WebAppDevelopmentPage'));
const MVPDevelopmentPage        = lazy(() => import('./pages/services/MVPDevelopmentPage'));
const CustomSoftwarePage        = lazy(() => import('./pages/services/CustomSoftwarePage'));
const CRMDevelopmentPage        = lazy(() => import('./pages/services/CRMDevelopmentPage'));
const ERPDevelopmentPage        = lazy(() => import('./pages/services/ERPDevelopmentPage'));
const LMSDevelopmentPage        = lazy(() => import('./pages/services/LMSDevelopmentPage'));
const AndroidAppDevelopmentPage = lazy(() => import('./pages/services/AndroidAppDevelopmentPage'));

// Cities
const DelhiPage     = lazy(() => import('./pages/cities/DelhiPage'));
const MumbaiPage    = lazy(() => import('./pages/cities/MumbaiPage'));
const BangalorePage = lazy(() => import('./pages/cities/BangalorePage'));
const HyderabadPage = lazy(() => import('./pages/cities/HyderabadPage'));
const PunePage      = lazy(() => import('./pages/cities/PunePage'));
const ChennaiPage   = lazy(() => import('./pages/cities/ChennaiPage'));
const KolkataPage   = lazy(() => import('./pages/cities/KolkataPage'));
const AhmedabadPage = lazy(() => import('./pages/cities/AhmedabadPage'));
const NoidaPage     = lazy(() => import('./pages/cities/NoidaPage'));
const GurgaonPage   = lazy(() => import('./pages/cities/GurgaonPage'));

// CRM
const CRMOverviewPage     = lazy(() => import('./pages/crm/CRMOverviewPage'));
const SchoolManagementCRM = lazy(() => import('./pages/crm/SchoolManagementCRM'));

// ─── Shared suspense fallback ─────────────────────────────────────────────────
function PageLoader() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <div className="w-10 h-10 border-4 border-emerald-200 dark:border-emerald-800 border-t-emerald-600 dark:border-t-emerald-400 rounded-full animate-spin" />
    </div>
  );
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function AdminBodyClass() {
  const { pathname } = useLocation();
  useEffect(() => {
    if (pathname === '/admin') {
      document.body.classList.add('admin-page');
    } else {
      document.body.classList.remove('admin-page');
    }
    return () => { document.body.classList.remove('admin-page'); };
  }, [pathname]);
  return null;
}

function PublicLayout() {
  return (
    <div className="min-h-screen flex flex-col">
      <CursorSpotlight />
      <Header />
      <main className="flex-1">
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/services" element={<ServicesPage />} />
            <Route path="/portfolio" element={<PortfolioPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blog/how-to-build-a-saas-platform" element={<HowToBuildSaaSPlatform />} />
            <Route path="/blog/saas-development-cost" element={<SaaSDevelopmentCost />} />
            <Route path="/blog/best-tech-stack-for-saas-startups-2025" element={<BestTechStackSaaS2025 />} />
            <Route path="/blog/10-profitable-micro-saas-ideas-2025" element={<MicroSaaSIdeas2025 />} />
            <Route path="/blog/firebase-vs-supabase" element={<FirebaseVsSupabase />} />
            <Route path="/blog/how-to-choose-software-development-company" element={<ChooseSoftwareDevelopmentCompany />} />
            <Route path="/blog/scalable-web-application-architecture" element={<ScalableWebAppArchitecture />} />
            <Route path="/blog/mvp-vs-full-product-development" element={<MVPvsFullProduct />} />
            <Route path="/blog/ai-saas-product-ideas-2025" element={<AISaaSIdeas2025 />} />
            <Route path="/blog/web-application-vs-website" element={<WebAppVsWebsite />} />
            <Route path="/blog/custom-crm-development-guide" element={<CRMDevelopmentGuide />} />
            <Route path="/blog/custom-erp-development-guide" element={<ERPDevelopmentGuide />} />
            <Route path="/blog/android-app-development-guide" element={<AndroidAppDevelopmentGuide />} />
            <Route path="/blog/software-development-company-india" element={<SoftwareDevelopmentCompanyIndia />} />
            <Route path="/blog/software-development-company-patna-bihar" element={<SoftwareDevelopmentCompanyPatna />} />
            <Route path="/blog/lms-development-guide" element={<LMSDevelopmentGuide />} />
            <Route path="/services/saas-development" element={<SaaSDevelopmentPage />} />
            <Route path="/services/web-application-development" element={<WebAppDevelopmentPage />} />
            <Route path="/services/mvp-development" element={<MVPDevelopmentPage />} />
            <Route path="/services/custom-software-development" element={<CustomSoftwarePage />} />
            <Route path="/services/crm-development" element={<CRMDevelopmentPage />} />
            <Route path="/services/erp-development" element={<ERPDevelopmentPage />} />
            <Route path="/services/lms-development" element={<LMSDevelopmentPage />} />
            <Route path="/services/android-app-development" element={<AndroidAppDevelopmentPage />} />
            <Route path="/web-development-company-delhi" element={<DelhiPage />} />
            <Route path="/web-development-company-mumbai" element={<MumbaiPage />} />
            <Route path="/web-development-company-bangalore" element={<BangalorePage />} />
            <Route path="/web-development-company-hyderabad" element={<HyderabadPage />} />
            <Route path="/web-development-company-pune" element={<PunePage />} />
            <Route path="/web-development-company-chennai" element={<ChennaiPage />} />
            <Route path="/web-development-company-kolkata" element={<KolkataPage />} />
            <Route path="/web-development-company-ahmedabad" element={<AhmedabadPage />} />
            <Route path="/web-development-company-noida" element={<NoidaPage />} />
            <Route path="/web-development-company-gurgaon" element={<GurgaonPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/google-my-business-services" element={<GoogleMyBusinessPage />} />
            <Route path="/crm" element={<CRMOverviewPage />} />
            <Route path="/crm/school-management" element={<SchoolManagementCRM />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/terms-and-conditions" element={<TermsAndConditionsPage />} />
          </Routes>
        </Suspense>
      </main>
      <Footer />
    </div>
  );
}

function AppRoutes() {
  const [session, setSession] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
    });

    return () => subscription?.unsubscribe();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-emerald-200 dark:border-emerald-800 border-t-emerald-600 dark:border-t-emerald-400 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <>
      <ScrollToTop />
      <AdminBodyClass />
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/admin" element={session ? <AdminPanel /> : <AdminLoginPage />} />
          <Route path="*" element={<PublicLayout />} />
        </Routes>
      </Suspense>
    </>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <ScrollProgress />
        <AppRoutes />
      </BrowserRouter>
    </ThemeProvider>
  );
}
